import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import TransactionRow from "@/components/TransactionRow";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from "react-i18next";
import { Receipt, Search, Wallet } from "lucide-react";
import { Input } from "@/components/ui/input";

interface Transaction {
  id: string;
  crypto_symbol: string;
  crypto_name: string;
  amount: number;
  price_usd: number;
  total_usd: number;
  card_last_four: string;
  card_type: string;
  status: string;
  created_at: string;
  wallet_address?: string;
  network?: string;
  network_fee?: number;
  transaction_number?: string;
}

const Transactions = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/auth");
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    const fetchTransactions = async () => {
      if (!user) return;
      const { data, error } = await supabase
        .from("transactions")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });
      if (!error && data) {
        setTransactions(data as Transaction[]);
      }
      setLoading(false);
    };
    fetchTransactions();
  }, [user]);

  const filteredTransactions = transactions.filter(
    (tx) =>
      tx.crypto_symbol.toLowerCase().includes(search.toLowerCase()) ||
      tx.crypto_name.toLowerCase().includes(search.toLowerCase()) ||
      tx.transaction_number?.toLowerCase().includes(search.toLowerCase())
  );

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background gap-4">
        <div className="w-16 h-16 rounded-xl gradient-primary flex items-center justify-center animate-spin-slow">
          <Wallet className="w-8 h-8 text-primary-foreground" />
        </div>
        <p className="text-muted-foreground text-sm">{t('common.loading')}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/3 w-[600px] h-[600px] bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/3 w-[600px] h-[600px] bg-crypto-purple/5 rounded-full blur-3xl" />
      </div>

      <main className="container mx-auto px-4 pt-24 pb-12 relative z-10">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8 animate-fade-in">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2">{t('transactions.title')}</h1>
            <p className="text-muted-foreground">{t('transactions.subtitle')}</p>
          </div>
          <div className="relative w-full md:w-80">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              placeholder={t('transactions.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="bg-input border-border text-foreground pr-10"
            />
          </div>
        </div>

        {filteredTransactions.length > 0 ? (
          <div className="space-y-4">
            {filteredTransactions.map((transaction, index) => (
              <div key={transaction.id} style={{ animationDelay: `${index * 50}ms` }}>
                <TransactionRow transaction={transaction} />
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-20 animate-fade-in">
            <div className="w-20 h-20 rounded-full bg-secondary flex items-center justify-center mb-4">
              <Receipt className="w-10 h-10 text-muted-foreground" />
            </div>
            <h3 className="text-xl font-semibold text-foreground mb-2">{t('transactions.noTransactions')}</h3>
            <p className="text-muted-foreground text-center">
              {search ? t('transactions.noSearchResults') : t('transactions.noTransactionsHint')}
            </p>
          </div>
        )}
      </main>
    </div>
  );
};

export default Transactions;
